import { getHotVideoDetail } from "~/api/task"
import {
  DEFAULT_VIDEO_CHANNEL,
  getSafeVideoDuration,
  HotVideoTaskStatus,
  HotVideoAudioType,
  isTaskStepFailedStatus,
} from "../constants"
import { pollHotVideoTask } from "./hotVideoTask"
import { getTaskVideoChannel } from "./taskFrontendRemark"
import type { UploadedResource } from "../media/uploadedResources"
import { DEFAULT_BACKGROUND_MUSIC_MODE } from "../video/recreate"

export type RecreateTaskDraft = {
  kind: "recreate"
  canSavePrompt: boolean
  sourceVideoUrl: string
  productImageUrls: string[]
  productInfo: API.Task.OptimizeProductInfo | null
  parsedImageUrls: string[]
  start_time: number
  end_time: number
  model_figure: API.FrameCopy.ModelFigureInfo | null
  model_figure_artline: API.Task.ModelFigureArtlineInfo | null
  timbre_url: string
  sourcePrompt?: string
  replicaPrompt?: string
  duration?: number
  model?: API.Task.CreateVideoChannel
  ratio?: API.VideoRatio
  resolution?: API.VideoResolution
  backgroundMusicMode?: "generate" | "original"
  errorMessage?: string
}

type FetchTaskDraftOptions = {
  isStale?: () => boolean
  onDraft?: (draft: RecreateTaskDraft) => void | Promise<void>
  onPollingStart?: () => void
}

const HOT_VIDEO_PROMPT_POLLING_STATUSES = new Set<HotVideoTaskStatus>([
  HotVideoTaskStatus.parseVideoProcessing,
  HotVideoTaskStatus.parseVideoSucceeded,
  HotVideoTaskStatus.optimizePromptProcessing,
])

const HOT_VIDEO_PROMPT_FAILED_STATUSES = new Set<HotVideoTaskStatus>([
  HotVideoTaskStatus.parseVideoFailed,
  HotVideoTaskStatus.optimizePromptFailed,
])

const getOptionalText = (value: unknown) => {
  return typeof value === "string" && value.trim() ? value : undefined
}

// 脚本生成完成、尚未提交视频时，详情页展示并允许保存 optimize_prompt
const canSaveHotVideoPrompt = (task: API.Task.HotVideoTask) =>
  Boolean(task.optimize_prompt) &&
  (task.status === HotVideoTaskStatus.optimizePromptSucceeded ||
    task.status === HotVideoTaskStatus.hidden)

// 任务列表与详情页共用：可编辑时取脚本；否则与高还原度复刻一致，
// 在视频提示词和脚本中取最后完成的一个
export const getHotVideoDisplayPrompt = (
  task: API.Task.HotVideoTask,
): string => {
  const optimizePrompt = task.optimize_prompt?.result_prompt ?? ""
  if (canSaveHotVideoPrompt(task)) return optimizePrompt

  const scriptPrompt = getOptionalText(optimizePrompt)
  const videoPrompt = getOptionalText(task.create_video?.prompt)
  if (!scriptPrompt || !videoPrompt) return videoPrompt || scriptPrompt || ""

  const scriptFinishedAt =
    Date.parse(task.optimize_prompt?.finished_t || "") || 0
  const videoFinishedAt = Date.parse(task.create_video?.finished_t || "") || 0

  return videoFinishedAt > scriptFinishedAt ? videoPrompt : scriptPrompt
}

const getHotVideoTaskErrorMessage = (task: API.Task.HotVideoTask) => {
  const failedStep = [
    task.replace_audio,
    task.create_video,
    task.optimize_prompt,
    task.parse_video,
  ].find((step) => step && isTaskStepFailedStatus(step.status))

  return getOptionalText(failedStep?.error_message)
}

const getDuration = (value: unknown, model?: API.Task.CreateVideoChannel) => {
  if (typeof value !== "number") return undefined

  return getSafeVideoDuration(model ?? DEFAULT_VIDEO_CHANNEL, value)
}

const getCreatedAt = (index: number) => {
  return Date.now() + index
}

const getHotVideoImageResources = (task: API.Task.HotVideoTask) => {
  const imageUrls = task.create_video?.image_urls?.length
    ? task.create_video.image_urls
    : task.optimize_prompt?.image_urls || []

  return imageUrls.map((url, index) => {
    return {
      name: `图片${index + 1}`,
      type: "image/jpeg",
      uploadedAt: getCreatedAt(index),
      url,
    } satisfies UploadedResource
  })
}

const getHotVideoAudioResources = (task: API.Task.HotVideoTask) => {
  const audioUrls = (task.create_video?.audio_urls || []).filter(
    (url, index, urls) => {
      return Boolean(url) && urls.indexOf(url) === index
    },
  )

  return audioUrls.map((url, index) => {
    return {
      name: `音频${index + 1}`,
      type: "audio/wav",
      uploadedAt: getCreatedAt(index),
      url,
    } satisfies UploadedResource
  })
}

export const getHotVideoReferenceResources = (task: API.Task.HotVideoTask) => {
  const imageResources = getHotVideoImageResources(task)
  const audioResources = getHotVideoAudioResources(task)

  return [...imageResources, ...audioResources]
}

export const getHotVideoSourceVideoUrl = (task: API.Task.HotVideoTask) => {
  return task.trim_video?.video_url || task.parse_video?.video_url || ""
}

export const getHotVideoTaskResultVideoUrl = (task: API.Task.HotVideoTask) => {
  return task.create_video?.video_url || ""
}

const buildRecreateTaskDraft = (
  task: API.Task.HotVideoTask,
): RecreateTaskDraft => {
  const optimizePrompt = task.optimize_prompt
  const createVideo = task.create_video
  const productImageUrls = createVideo?.image_urls?.length
    ? createVideo.image_urls
    : optimizePrompt?.image_urls || []
  const model = getTaskVideoChannel(task)
  const canSavePrompt = canSaveHotVideoPrompt(task)

  return {
    kind: "recreate",
    canSavePrompt,
    backgroundMusicMode:
      (createVideo?.audio_type ?? task.audio_type) ===
      HotVideoAudioType.original
        ? "original"
        : DEFAULT_BACKGROUND_MUSIC_MODE,
    duration:
      getDuration(createVideo?.duration, model) ??
      getDuration(optimizePrompt?.duration, model),
    errorMessage: getHotVideoTaskErrorMessage(task),
    model,
    productImageUrls,
    productInfo: optimizePrompt?.product_info ?? null,
    parsedImageUrls: optimizePrompt?.image_urls || [],
    start_time: task.trim_video?.start_time ?? 0,
    end_time: task.trim_video?.end_time ?? 0,
    model_figure: task.model_figure ?? null,
    model_figure_artline: task.model_figure_artline ?? null,
    timbre_url: task.timbre_url || "",
    replicaPrompt: getHotVideoDisplayPrompt(task),
    ratio: createVideo?.ratio,
    resolution: createVideo?.resolution,
    sourcePrompt: getOptionalText(task.parse_video?.result_text),
    sourceVideoUrl: getHotVideoSourceVideoUrl(task),
  }
}

export async function fetchTaskDraft(
  taskId: string,
  options: FetchTaskDraftOptions = {},
): Promise<RecreateTaskDraft | null> {
  if (!taskId.trim()) return null

  const response = await getHotVideoDetail({ task_id: taskId })
  let task = response.data

  if (!task || options.isStale?.()) return null

  let draft = buildRecreateTaskDraft(task)
  await options.onDraft?.(draft)
  if (options.isStale?.()) return null

  if (!HOT_VIDEO_PROMPT_POLLING_STATUSES.has(task.status)) return draft

  options.onPollingStart?.()
  task = await pollHotVideoTask({
    getErrorMessage: (nextTask) =>
      nextTask.parse_video?.error_message ||
      nextTask.optimize_prompt?.error_message ||
      "AI拆解融合失败，请稍后重试",
    isDone: (nextTask) =>
      nextTask.status === HotVideoTaskStatus.optimizePromptSucceeded ||
      Boolean(nextTask.optimize_prompt?.result_prompt),
    isFailed: (nextTask) =>
      HOT_VIDEO_PROMPT_FAILED_STATUSES.has(nextTask.status),
    isStale: options.isStale,
    taskId,
    timeoutMessage: "AI拆解融合超时，请稍后重试",
  })

  if (!task || options.isStale?.()) return null

  draft = buildRecreateTaskDraft(task)
  await options.onDraft?.(draft)
  if (options.isStale?.()) return null

  return draft
}
